import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { debounce } from 'debounce';
import cx from 'classnames';
// containers
import { MapSettingsContainer } from 'src/containers/MapSettingsContainer';
import { AddressSearchContainer } from 'src/app/containers/AddressSearchContainer';
// common components
import { StyledMap, MapControl, GeocoderPin, RulerTool } from '@ems/client-design-system';
import { MapReferenceLayers } from 'src/app/components';
import { LocationPopup, OperationMapTags } from 'src/components';
// functions
import { useMapSettings, useRerunHookOnMapBackgroundChange } from 'src/app/functions/mapSettings';
import {
  useMapRef,
  useMapWhenReady,
  useMapProps,
  useMapConfig,
  useMapCenter,
  useSingleDateTile,
} from 'src/app/functions/map';
import {
  useMapLayer,
  useInfringementMap,
  useMapTracksFilter,
} from 'src/app/functions/infringementsOnMap';
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
import {
  flyTo,
  useHoverOnMapElement,
  useMapHover,
  useMapClick,
  useMapSelection,
  useGeometryRequiredByMap,
  useHoveredPointData,
  useMapStylesForPlayback,
  useMapRuler,
  withOperationTrack,
} from 'src/utils';

import { AMSLPopup } from 'src/components';
import { useMapReftoCaptureImage } from 'src/app/functions/export';
import { useInAirPlayback, useNoiseMonitorPlayback, useStaticDbDisplay } from 'src/utils/playback';
import { NoisePopup } from 'src/components/NoisePopup';
import {
  addPinToCentre,
  getStoredMarkers,
  goToSelectedAddress,
  onGeocodingDragEnd,
  useGeocodePosition,
} from 'src/utils/geocoding';
import { useCircleRanges } from 'src/app/functions/rangeCircle';
// constants
import { TOGGLE_MAP_SETTINGS_CTRL, DYNAMIC_TILE_SERVER } from 'src/app/featureToggles';
import { MAP_TYPES } from 'src/constants';

import { IMapData, ITrackInteractionPoint } from 'src/utils/interfaces';
import { dateTimeInQueryUTC } from 'src/utils/dateTimeConverters';
import { vectorLayerToPoint, profileTimeToMapVectorLayer } from 'src/utils/mapTagHelpers';

import { useGeometrySelectors } from 'src/app/reducers';
import { MapLegend } from 'src/components/MapLegend';

import { useTrackSelection } from 'src/utils/mapHelpers/trackHelpers/useTrackSelection';
import { useOperationTrackData } from 'src/utils/mapHelpers/trackHelpers/useOperationTrackData';
import { useMapTags } from 'src/utils/mapHelpers/tags/useMapTags';
import { IRuleData } from 'src/@infringements/interfaces';

export const InfringementDetailsMap: FC<{
  time: string;
  operationId: string;
  operation: any;
  infTypeId: number;
  infringementType: string;
  infringementId: string;
  position: any;
  isPlaybackMode?: boolean;
  currentTime: number;
  inAirData?: any;
  noiseData: any;
  processedNoiseData: any;
  noiseMonitors: any;
  extraIds: number[];
  setSelectedTime: any;
  profileHoverTime: number | null;
  isPlaybackRunning: boolean;
  profileClickTime: number | null;
  ruleData: IRuleData;
}> = ({
  time,
  operationId,
  operation,
  infTypeId,
  infringementType,
  infringementId,
  position,
  isPlaybackMode = false,
  currentTime,
  inAirData,
  noiseData,
  processedNoiseData,
  noiseMonitors,
  extraIds,
  setSelectedTime,
  profileHoverTime,
  isPlaybackRunning,
  profileClickTime,
  ruleData,
}) => {
  // get map props from config
  const { viewportFromProps, mapStyle: defaultMapStyle, ...mapProps } = useMapProps('2D');
  // map settings
  const {
    mapStyle,
    storeSelectedBackground,
    applyBackground,
    resetBackground,
    layersDisplayed,
    storeSelectedLayers,
    applyLayers,
    resetLayers,
  } = useMapSettings({
    background: defaultMapStyle,
    layers: [],
  });

  // used for taking screenshot of map
  const captureRef = useRef(null);
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const { mapApis, mapLoaded } = useMapWhenReady(mapNode);
  // viewport in state
  const [viewport, setViewport] = useState(viewportFromProps);

  // Op start/end times

  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();
  // Configuration
  const configSelectors = useConfigSelectors();
  // get field labels from language selectors
  const languageSelectors = useLanguageSelectors();
  const {
    fields: { operations: opsFields },
    components: {
      headings: { mapSettings: mapSettingsTitle },
      labels: {
        lat: latLabel,
        lng: lngLabel,
        amsl: amslLabel,
        backToCenter: backToCenterLabel,
        ruler: ruler,
      },
    },
    abbreviations,
  } = languageSelectors.getLanguage();

  const {
    map: { mapProjectionString },
  } = configSelectors.getConfig();

  const labels = Object.assign(opsFields, abbreviations);

  // capture map image
  const { enableMapControls } = useMapReftoCaptureImage(captureRef, mapApis);

  useMapCenter(viewport, setViewport, position, operationId);
  // getting map style layers
  const layers = useMapLayer({ mapApis, mapBoxConfig, maptype: 'infringements' });

  const rerunHook = useRerunHookOnMapBackgroundChange(mapApis, mapStyle, 2000);

  useGeometryRequiredByMap({
    mapApis,
    mapBoxConfig,
    infringementTypes: typeof infringementType !== 'undefined' ? [infringementType] : [],
    dateRange: {
      dateFilterFrom: dateTimeInQueryUTC(new Date(time), 'start'),
      dateFilterTo: dateTimeInQueryUTC(new Date(time), 'end'),
    },
    mapStyle,
  });

  const FEATURE_FLAG_DYNAMIC_TILE_SERVER = configSelectors.isFeatureAvailable(DYNAMIC_TILE_SERVER);

  // The in-air feed is another snapshot query at the infringement time, so it
  // can come back without the infringing operation - keep its own track in the
  // batch regardless (ALN-1637). Memoised because useSingleDateTile treats
  // mapTrackData as an effect dependency.
  const mapTrackData = useMemo(() => withOperationTrack<IMapData>(inAirData, operation), [
    inAirData,
    operation,
  ]);

  const { dateString, datesArray } = useSingleDateTile({
    mapApis,
    mapBoxConfig,
    layers,
    time,
    operationId,
    isDynamicTileServer: FEATURE_FLAG_DYNAMIC_TILE_SERVER,
    mapTrackData,
  });

  const { tracks } = useInfringementMap({
    instance: 'inf-summary',
    mapApis,
    mapBoxConfig,
    dateString,
    operationId,
    operation,
    infTypeId,
    infringementId,
    infringementType,
    showTracks: true,
    time,
    extraIds,
    rerunHook,
  });

  useMapStylesForPlayback({ mapApis, mapBoxConfig, dateString, isPlaybackMode });

  // extrack map filters for the features
  const geometrySelectors = useGeometrySelectors();
  const noiseMonitorLocations = geometrySelectors.getMonitorLocations();
  const { infringementFilter } = useMapTracksFilter({ features: tracks, noiseMonitorLocations });

  // handle hover and get hovered operation
  const { hoveredElement, handleHover, setHoveredElement } = useHoverOnMapElement({
    viewport,
    mapApis,
    layerArray: datesArray,
    tracksFilter: infringementFilter,
    restrictZoomLevels: false,
    mapType: MAP_TYPES.INFRINGEMENTSUMMARY,
    rerunHook,
    featureStateName: 'selected',
    preferFeatureState: true,
  });
  // set map selection for the hover object
  useMapHover(hoveredElement, mapApis);

  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  // Map selection
  const selectedOperationId = useMemo(() => [Number(operationId)], []);
  const operationTrackData = useOperationTrackData(mapApis, datesArray, selectedOperationId);
  const { setSelectedOperations, selectedOperations } = useTrackSelection({
    mapApis,
    externallySelectedTracks: operationTrackData,
    rerunHook,
  });
  const [showSelected, setShowSelected] = useState<boolean>(false);
  const [selectedPointData, setSelectedPointData] = useState<ITrackInteractionPoint>({
    amsl: null,
    time: null,
    longitude: null,
    latitude: null,
    showPointData: false,
    flightId: null,
  });

  const {
    displayedMapTags,
    addTagToMap,
    clearDisplayedTags,
    removeDisplayedTag,
    addTagOnTrackClick,
  } = useMapTags();

  // click handlers that provide selected operations
  const { handleClick } = useMapClick({
    hoveredOperation: hoveredElement,
    setShowSelected,
    mapApis,
    datesArray,
    tracksFilter: infringementFilter,
    selectedOperations,
    setSelectedOperations: addTagOnTrackClick,
    mapType: MAP_TYPES.INFRINGEMENTSUMMARY,
    clearDisplayedTags,
    featureStateName: 'selected',
  });

  useHoveredPointData({
    mapApis,
    operation,
    nearbyFlightsData: inAirData,
    hoveredElement,
    profileHoverTime,
    setSelectedTime,
    setSelectedPointData,
    isPlaybackMode,
    isPlaybackRunning,
    userHomeLocation: null,
    mapProjectionString: null,
  });

  // set feature state for the hovered operation
  useMapSelection(selectedOperations, mapApis, 'infringement-select');

  // reset hover and select when selection changes
  useEffect(() => {
    setShowSelected(false);
    setHoveredElement(null);
    setSelectedOperations([]);
  }, [dateString]);

  useEffect(() => {
    if (profileClickTime) {
      const selectedLayers = profileTimeToMapVectorLayer(mapApis, operation, profileClickTime);
      const operationLayer = selectedLayers.find(layer => layer.id.toString() === operationId);
      // Allow tags to be displayed
      if (showSelected === false) {
        setShowSelected(true);
      }
      addTagToMap(operationLayer);
    }
  }, [profileClickTime]);

  // Playback Section
  const dataSources = useMemo(() => (operation ? [operation, ...inAirData] : inAirData), [
    operation,
    inAirData,
  ]);
  useInAirPlayback(mapApis, dataSources, currentTime, isPlaybackMode, Number(operationId));
  const staticMonitors = useStaticDbDisplay(mapApis, noiseMonitors, noiseData, isPlaybackMode);

  const selectedMonitor = useNoiseMonitorPlayback({
    mapApis,
    noiseMonitors,
    noiseData,
    processedNoiseData,
    currentTime,
    isPlaybackMode,
  });

  const [noisePopups, setNoisePopups] = useState<JSX.Element[]>([]);
  useEffect(() => {
    if (mapApis) {
      if (selectedMonitor.length > 0 || staticMonitors.length > 0) {
        const popups: JSX.Element[] = [];
        const mapFn = (monitor: any) => {
          popups.push(
            <NoisePopup
              key={`popup_${monitor.properties.id}`}
              zoomLevel={mapApis.getZoom()}
              monitorData={monitor}
              ruleData={ruleData}
              noiseData={noiseData}
              isPlaybackMode={isPlaybackMode}
            />
          );
        };
        if (isPlaybackMode) {
          selectedMonitor.map(mapFn);
        } else {
          staticMonitors.map(mapFn);
        }
        setNoisePopups(popups);
      }
    }
  }, [mapApis, selectedMonitor, staticMonitors, rerunHook, isPlaybackMode]);
  // End Playback Section

  // Ruler Tool

  const units = configSelectors.getUnits();

  const { rulerCoordinatesChanged, toggleRuler, isRulerEnabled, rulerCoordinates } = useMapRuler({
    mapApis,
    viewport,
  });

  // restrict map pan
  const onViewportChange = viewport => {
    if (
      Math.abs(viewport.latitude - viewportFromProps.latitude) < mapBoxConfig.limitLatitude &&
      Math.abs(viewport.longitude - viewportFromProps.longitude) < mapBoxConfig.limitLongitude
    ) {
      setViewport(viewport);
    }
  };

  const client = useApolloClient();
  const { addRemoveCircles } = useCircleRanges(mapApis, mapStyle);
  const storedMarkers = getStoredMarkers();
  const [geocoding, updateGeocoding] = useState<{ longitude: number; latitude: number }>(
    storedMarkers ? storedMarkers.main : { longitude: 0, latitude: 0 }
  );
  const [locationAddress, updateLocationAddress] = useState<null | string>(null);
  const { latitude, longitude } = geocoding;
  const { elevation, place } = useGeocodePosition({
    client,
    position: {
      longitude,
      latitude,
    },
  });
  const [closeSearch, updateCloseSearch] = useState<boolean>(false);
  const [isLocationTagOpen, updateLocationTagOpen] = useState<boolean>(false);
  const [drag, updateDragStatus] = useState<boolean>(false);
  const { removeHovered } = useMapHover(hoveredElement, mapApis, drag);

  const AddressSearch = useMemo(
    () => (
      <div className="mapboxgl-ctrl-search">
        <AddressSearchContainer
          source="map"
          onAddressFound={address =>
            goToSelectedAddress({
              address,
              mapApis,
              viewport,
              addRemoveCircles,
              updateGeocoding,
              updateLocationAddress,
              updateLocationTagOpen,
              onViewportChange,
              updateCloseSearch,
            })
          }
        />
      </div>
    ),
    [mapApis, addRemoveCircles]
  );

  const currentLayout = configSelectors.getLayout();
  const isFullScreen = configSelectors.getIsFullscreen();
  const isMapFullscreen = isFullScreen && currentLayout.includes('MAP');
  const isGridFullscreen = isFullScreen && currentLayout.includes('GRID');
  const mapHeight = isMapFullscreen ? 'calc(100vh - 2rem)' : undefined;

  return (
    <div
      className={cx('map_wrapper', {
        'map_wrapper--fullscreen': isMapFullscreen,
        'map_wrapper--collapsed': isGridFullscreen,
      })}>
      <div ref={captureRef} className="map">
        <StyledMap
          onLoad={() => mapLoaded()}
          viewport={viewport}
          mapStyle={mapStyle}
          onViewportChange={viewport => {
            viewport.maxPitch = 0;
            setViewport(viewport);
          }}
          {...mapProps}
          ref={mapRef}
          onClick={handleClick}
          onHover={debounce(handleHover, 5)}
          transformRequest={
            mapBoxConfig && mapBoxConfig.transformRequest && mapBoxConfig.transformRequest()
          }
          height={mapHeight}>
          {isLocationTagOpen && (
            <LocationPopup
              latitude={latitude}
              longitude={longitude}
              address={locationAddress || place}
              elevation={elevation}
              languageData={{ latLabel, lngLabel, amslLabel }}
              mapApis={mapApis}
              showFilterButton={false}
              onClose={() => {
                updateLocationTagOpen(!isLocationTagOpen);
              }}
            />
          )}
          <GeocoderPin
            latitude={enableMapControls ? latitude : 0}
            longitude={enableMapControls ? longitude : 0}
            draggable
            mapApis={mapApis}
            addRemoveCircles={addRemoveCircles}
            onClick={() => {
              updateLocationTagOpen(!isLocationTagOpen);
            }}
            onDragStart={() => {
              addRemoveCircles(null);
              removeHovered();
              updateDragStatus(true);
              updateLocationTagOpen(false);
            }}
            onDragEnd={([longitude, latitude]: number[]) =>
              onGeocodingDragEnd({
                longitude,
                latitude,
                updateDragStatus,
                updateGeocoding,
                updateLocationAddress,
                updateLocationTagOpen,
                addRemoveCircles,
              })
            }
            onMouseEnter={() => {
              removeHovered();
              updateDragStatus(true);
            }}
            onMouseLeave={() => {
              updateDragStatus(false);
            }}
          />
          {enableMapControls && (
            <MapControl
              isPinAdded={latitude && longitude ? true : false}
              addPinToCentre={() =>
                addPinToCentre({
                  updateLocationAddress,
                  geocoding,
                  viewport,
                  updateGeocoding,
                  addRemoveCircles,
                  updateLocationTagOpen,
                })
              }
              translationData={{
                home: backToCenterLabel,
                mapSettings: mapSettingsTitle,
                ruler,
              }}
              rulerControl={{
                isRulerEnabled,
                toggleRuler,
              }}
              navigationControl={{
                showCompass: false,
                showHome: true,
                showSearch: true,
                showSettings: configSelectors.isFeatureAvailable(TOGGLE_MAP_SETTINGS_CTRL),
              }}
              addressSearch={AddressSearch}
              closeSearch={closeSearch}
              resetView={resetView}
              mapSettingsConfig={{
                update: () => {
                  applyBackground();
                  applyLayers();
                },
                reset: () => {
                  resetBackground();
                  resetLayers();
                },
                content: (
                  <MapSettingsContainer
                    config={{
                      background: mapStyle,
                      layers: layersDisplayed,
                    }}
                    onUpdate={({ selectedBackground, selectedLayers }) => {
                      if (typeof selectedBackground !== 'undefined') {
                        storeSelectedBackground(selectedBackground);
                      }
                      if (typeof selectedLayers !== 'undefined') {
                        storeSelectedLayers(selectedLayers);
                      }
                    }}
                  />
                ),
              }}
            />
          )}

          {noisePopups}

          {displayedMapTags.map(({ data, uuid }) => {
            const matchingOperation = inAirData.find(
              operation => operation.id === data.properties.id || operation.id === data.id
            );
            return (
              <OperationMapTags
                element={data}
                pointData={vectorLayerToPoint({
                  operation: matchingOperation,
                  clickedElement: data,
                })}
                labels={labels}
                draggable
                onClose={() => removeDisplayedTag(uuid)}
                key={uuid}
              />
            );
          })}

          {hoveredElement && <AMSLPopup labels={labels} pointData={selectedPointData} />}
          <MapReferenceLayers
            mapApis={mapApis}
            mapStyle={mapStyle}
            layers={layersDisplayed}
            dateRange={{
              dateFilterFrom: dateTimeInQueryUTC(new Date(time), 'start'),
              dateFilterTo: dateTimeInQueryUTC(new Date(time), 'end'),
            }}
          />
          <RulerTool
            distanceUnits={units.distance}
            coordinates={rulerCoordinates}
            isRulerEnabled={isRulerEnabled}
            mapProjection={mapProjectionString}
            handleDragEvent={rulerCoordinatesChanged}
            mapApis={mapApis}
          />
          <MapLegend layersDisplayed={layersDisplayed} />
        </StyledMap>
      </div>
    </div>
  );
};
